'use client';
import { useParams } from 'next/navigation';
import React, { useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { SubHeadline, Text } from '~components/Typography';
import { routes } from '~constants/routes';
import { useAccountContext } from '~context/account';
import { usePaywallContext } from '~context/paywall';
import { NsfwReason } from '~features/annals/enums/nsfwReason';
import { PaymentReason } from '~features/annals/enums/payment';
import { RegisterInbound } from '~features/annals/enums/register';
import annalsCheckout from '~features/annals/hooks/checkout';
import annalsNavigation from '~features/annals/hooks/navigation';
import { useDiscordConnected } from '~features/discord/useDiscordConnected';
import { useLanguage } from '~features/language';
import {
	useSafeModeRedirect,
	useSwitchSafeMode,
	useSwitchSafeModeWithAgeValidation,
} from '~features/nsfw';
import { useIsDuplicateOwner } from '~hooks/useIsDuplicateOwner';
import { useSession } from '~hooks/useSession';
import { useTranslations } from '~i18n/hooks';
import { Link } from '~i18n/navigation';
import ExternalLinkIcon from '~public/assets/icons/navigation/externalLink.svg';
import LogoutIcon from '~public/assets/icons/navigation/logout.svg';
import WebIcon from '~public/assets/icons/navigation/web.svg';
import EyeNoneIcon from '~public/assets/icons/nsfw-eye-none.svg';
import EyeOpenIcon from '~public/assets/icons/nsfw-eye-open.svg';
import ProfileIcon from '~public/assets/icons/user-circle.svg';
import { userApi } from '~services/api/user';
import { ButtonSecondary } from '~shared/ui/atoms/Buttons/ButtonSecondary';
import {
	Dropdown,
	DropdownElement,
	DropdownSeparator,
} from '~shared/ui/atoms/Dropdown';
import { NeuronImage } from '~shared/ui/atoms/NeuronImage';
import { UserAvatar } from '~shared/ui/organisms/UserAvatar';
import type { AppDispatch } from '~store/index';
import {
	getIsModalShownForRegistered,
	safeModeModalActions,
} from '~store/safe-mode-modal';
import { logout } from '~store/utils/logout';
import { getAppUrl, getSubdomainUrlStr, isSafeApp } from '~utils/host';
import { useHeaderContext } from '~widgets/Header/context';

import { PromoHeadButton } from '../PromoHeadButton';
import { ConnectDiscordItem } from './ConnectDiscordItem';
import { MySubscriptionsItem } from './MySubscriptionsItem';
import { SettingsItem } from './SettingsItem';
import {
	avatarStyles,
	UserProfile,
	UserProfileLeft,
	UserProfileRight,
} from './styles';
import { SubscriptionItem } from './SubscriptionItem';

export const AuthContent = () => {
	const { recipientId: botClientId } = useParams<{ recipientId?: string }>();
	const dispatch = useDispatch<AppDispatch>();
	const t = useTranslations('ProfileMenu');
	const { language, locale } = useLanguage();
	const {
		userId,
		nick,
		isDemoUser,
		isSuccess: isSuccessSession,
		isFromJoiAI,
	} = useSession();
	const updateSafeMode = useSwitchSafeModeWithAgeValidation();
	const switchSafeMode = useSwitchSafeMode();
	const modalShownForRegistered = useSelector(getIsModalShownForRegistered);
	const { redirectToAppropriateDomain } = useSafeModeRedirect();
	const isDuplicateOwner = useIsDuplicateOwner();
	const showNSFWButton = false; // temporary, until purchases on joi.ai don't work

	const { hasSubscription, isSuccess, balance } = useAccountContext();
	const { discordConnected, isSuccess: isDiscordConnectedSuccess } =
		useDiscordConnected();
	const { languageOpen } = useHeaderContext();
	const { openPaywall } = usePaywallContext();

	const dropdownRef = useRef<HTMLButtonElement | null>(null);

	const { data: userInfo } = userApi.useUserInfoQuery(
		{ id: userId },
		{ skip: !userId || !isSuccessSession }
	);
	const { data } = userApi.useGetLevelQuery(
		{ userId },
		{ skip: isDemoUser || !userId || !isSuccessSession }
	);

	const reasonType = annalsCheckout.usePaymentReasonType();
	const annalsNavSignOutClicked =
		annalsNavigation.useNavTopSignOutButtonClicked();

	const appUrl = getAppUrl();
	const creatorUrl = getSubdomainUrlStr('creator', appUrl);

	const handleSwitchModeClick = async () => {
		try {
			if (modalShownForRegistered) {
				await switchSafeMode(!isSafeApp, {
					nsfwReason: NsfwReason.TopBanner,
				});
			} else {
				await updateSafeMode(!isSafeApp, {
					force: true,
					nsfwReason: NsfwReason.TopBanner,
					registerInbound: RegisterInbound.HeaderNsfwAction,
				});
				dispatch(safeModeModalActions.showForRegistered());
			}

			redirectToAppropriateDomain();
		} catch (e) {}
	};
	const triggerLogout = useCallback(() => {
		annalsNavSignOutClicked();
		dispatch(logout());
	}, [dispatch, annalsNavSignOutClicked]);

	return (
		<>
			{isSuccess && !hasSubscription && (
				<PromoHeadButton botClientId={botClientId} reasonType={reasonType} />
			)}
			{isSuccess && hasSubscription && (
				<ButtonSecondary
					data-name="neurons-button"
					size="M"
					leftIcon={<NeuronImage />}
					onClick={openPaywall({
						reason: PaymentReason.LeftMenu,
						reasonType,
						prefer: 'neurons',
						botClientId,
					})}
				>
					{balance > 0
						? new Intl.NumberFormat().format(balance)
						: t('addNeuronsButtonTitle')}
				</ButtonSecondary>
			)}

			<button ref={dropdownRef}>
				<UserAvatar className={avatarStyles} name={nick} />
			</button>

			<Dropdown targetRef={dropdownRef}>
				<UserProfile>
					<UserProfileLeft>
						<Text align="left" size="L">
							{userInfo?.name}
						</Text>
						<SubHeadline
							data-name="level-item"
							size="S"
							color="grey-52-solid"
							align="left"
						>
							{t('levelTitle')} {data?.level}
						</SubHeadline>
					</UserProfileLeft>
					<UserProfileRight>
						<UserAvatar className={avatarStyles} name={nick} />
					</UserProfileRight>
				</UserProfile>

				{!discordConnected && isDiscordConnectedSuccess && (
					<ConnectDiscordItem />
				)}
				{isSuccess && !hasSubscription && <SubscriptionItem />}
				{/* Если пользователь зарегистрировался на NSFW-сайте, ему доступен переход между сайтами SFW -NSFW в Auth zone через кнопку в меню */}
				{/* temporary, until purchases on joi.ai don't work */}
				{showNSFWButton && (!isSafeApp || !isFromJoiAI) && (
					<DropdownElement
						data-name="nsfw-item"
						hint={isSafeApp ? t('nsfwShow') : t('nsfwHide')}
						onClick={handleSwitchModeClick}
						icon={isSafeApp ? <EyeNoneIcon /> : <EyeOpenIcon />}
					>
						<Text size="M" align="left">
							{t('nsfwTitle')}
						</Text>
					</DropdownElement>
				)}

				<DropdownElement
					data-name="my-profile-item"
					href={routes.creator.slug(nick)}
					icon={<ProfileIcon />}
					as={Link}
				>
					<Text size="M" align="left">
						{t('myProfileTitle')}
					</Text>
				</DropdownElement>

				<SettingsItem />
				<MySubscriptionsItem />

				{isDuplicateOwner && (
					<DropdownElement
						data-name="digital-duplicate-item"
						href={creatorUrl}
						icon={<ExternalLinkIcon />}
						hint="Manage"
						as="a"
					>
						<Text size="M" align="left">
							{t('digitalDuplicateTitle')}
						</Text>
					</DropdownElement>
				)}
				<DropdownSeparator />
				<DropdownElement
					data-name="language"
					icon={<WebIcon />}
					onClick={languageOpen}
					hint={`${language} - ${locale.toUpperCase()}`}
				>
					<Text size="M" align="left">
						{t('languageTitle')}
					</Text>
				</DropdownElement>
				<DropdownSeparator />
				<DropdownElement
					data-name="sign-out-item"
					icon={<LogoutIcon />}
					onClick={triggerLogout}
				>
					<Text size="M" align="left">
						{t('signOut')}
					</Text>
				</DropdownElement>
			</Dropdown>
		</>
	);
};
